import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { invalidateSubscriptionCache } from '@/hooks/useOrganizationSubscription'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import { subscribeWorkspaceReadOnly } from '@/lib/billing/workspace-read-only'
import { cn } from '@/lib/utils'
import { LockSimpleIcon, WarningCircleIcon, XIcon } from '@phosphor-icons/react'
import { createPortal } from 'react-dom'
import { toast } from 'sonner'

import { TrialPhase } from '@scrambl/shared'

import { trialBannerCopy } from './trial-copy'
import { TrialActivationDialog } from './TrialActivationDialog'
import { useWorkspaceTrial } from './useWorkspaceTrial'

const ACTIVATION_PARAM = 'trial_activation'
const ACTIVATION_POLL_MS = 3000
const ACTIVATION_POLL_ATTEMPTS = 10

/**
 * Urgent banner in the grace period and a persistent one while the workspace
 * is read-only (docs/PLAN-no-card-trials.md). Also owns the two page-level
 * trial side effects: refreshing billing state when a write is refused, and
 * confirming the return from Stripe Checkout. Mounted once by OrgRouteGuard.
 */
export function WorkspaceTrialBanner() {
  const { trial, isAdmin, organizationId, organizationName, seats } =
    useWorkspaceTrial()
  const { formatDate } = useUserTimezone()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [dismissedGraceFor, setDismissedGraceFor] = useState<string | null>(
    null,
  )

  // A write refused by the lock means the payload we hold may be stale.
  useEffect(() => {
    if (!organizationId) return
    return subscribeWorkspaceReadOnly(() =>
      invalidateSubscriptionCache(organizationId),
    )
  }, [organizationId])

  // Back from Stripe Checkout: the webhook activates the workspace within
  // seconds; poll briefly so the chip and banner clear without a reload.
  const [activationOutcome] = useState(() =>
    new URL(window.location.href).searchParams.get(ACTIVATION_PARAM),
  )
  useEffect(() => {
    if (!activationOutcome || !organizationId) return
    const url = new URL(window.location.href)
    if (url.searchParams.has(ACTIVATION_PARAM)) {
      url.searchParams.delete(ACTIVATION_PARAM)
      window.history.replaceState(window.history.state, '', url.toString())
    }
    if (activationOutcome !== 'success') {
      toast.info('Checkout canceled — nothing was charged.', {
        id: 'trial-activation-return',
      })
      return
    }
    toast.success('Card added — Scrambl is active', {
      id: 'trial-activation-return',
      description: 'Thanks! Everyone in the workspace can keep working.',
    })
    let attempts = 0
    const timer = window.setInterval(() => {
      attempts += 1
      invalidateSubscriptionCache(organizationId)
      if (attempts >= ACTIVATION_POLL_ATTEMPTS) window.clearInterval(timer)
    }, ACTIVATION_POLL_MS)
    return () => window.clearInterval(timer)
  }, [activationOutcome, organizationId])

  if (!trial || !organizationId) return null
  if (trial.phase === TrialPhase.TRIALING) return null
  const isLocked = trial.phase === TrialPhase.LOCKED
  if (!isLocked && dismissedGraceFor === trial.graceEndsAt) return null

  const copy = trialBannerCopy(trial, {
    isAdmin,
    organizationName,
    graceDate: trial.graceEndsAt
      ? formatDate(trial.graceEndsAt, 'MMMM d')
      : undefined,
  })
  if (!copy) return null
  const Icon = isLocked ? LockSimpleIcon : WarningCircleIcon

  return createPortal(
    <>
      <div
        role={isLocked ? 'status' : 'alert'}
        data-trial-banner={trial.phase}
        className={cn(
          'fixed bottom-4 left-1/2 z-40 flex w-[min(40rem,calc(100vw-2rem))] -translate-x-1/2 items-start gap-3 rounded-xl border px-4 py-3 shadow-lg',
          isLocked
            ? 'border-destructive/40 bg-background text-foreground'
            : 'border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-500/40 dark:bg-amber-950 dark:text-amber-50',
        )}
      >
        <Icon
          aria-hidden
          weight="bold"
          className={cn(
            'mt-0.5 h-4 w-4 shrink-0',
            isLocked
              ? 'text-destructive'
              : 'text-amber-700 dark:text-amber-300',
          )}
        />
        <div className="min-w-0 flex-1">
          <p className="text-[length:var(--font-size-sm)] font-semibold">
            {copy.title}
          </p>
          <p className="mt-0.5 text-[length:var(--font-size-sm)]">
            {copy.body}
          </p>
        </div>
        {copy.action && (
          <Button
            size="sm"
            variant={isLocked ? 'default' : 'outline'}
            className="shrink-0"
            onClick={() => setIsDialogOpen(true)}
          >
            {copy.action}
          </Button>
        )}
        {!isLocked && (
          <button
            type="button"
            aria-label="Hide for now"
            className="shrink-0 rounded-md p-1 opacity-80 hover:opacity-100"
            onClick={() => setDismissedGraceFor(trial.graceEndsAt)}
          >
            <XIcon aria-hidden className="h-4 w-4" />
          </button>
        )}
      </div>
      {isDialogOpen && (
        <TrialActivationDialog
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          organizationId={organizationId}
          trial={trial}
          currentSeats={seats}
        />
      )}
    </>,
    document.body,
  )
}
